/**
 * Découpage administratif français — code postal → département → région.
 *
 * POURQUOI CE FICHIER (refonte annuaire 2026-08-06) : l'annuaire n'avait qu'un
 * seul étage géographique, la ville. Résultat, 33 pages de ville isolées, sans
 * page parente où Google (et les visiteurs) puissent circuler. HelloAsso, qui
 * a 450 000 associations, expose trois étages
 * (`/e/reg/<region>/dep/<dep>/ville/<ville>`) — c'est ce qui rend son annuaire
 * parcourable. On pose la même hiérarchie, avec des seuils qui empêchent les
 * pages vides tant que l'inventaire est faible.
 *
 * AUCUNE API EXTERNE : le département d'une association est la colonne
 * `address_department` (migration 307, calculée en base depuis le code INSEE
 * ou le code postal), à défaut `departementDepuisCodes` de `@donoor/utils`
 * sur l'analyse du texte (cf. villeAnnuaire.ts) — la même règle que la base.
 * La région vient d'une table statique. Rien à synchroniser, rien à payer.
 *
 * OUTRE-MER (lot 4, 2026-09-27) : chaque collectivité est une région ET un
 * département homonymes, sur le modèle des DROM. Saint-Barthélemy (977,
 * 97133) et Saint-Martin (978, 97150) ne sont plus rangés en Guadeloupe ;
 * Saint-Pierre-et-Miquelon (975), Wallis-et-Futuna (986), la Polynésie
 * française (987) et la Nouvelle-Calédonie (988) ont leurs pages. Noms,
 * slugs et prépositions viennent de la table UNIQUE `TERRITOIRES_OUTRE_MER`.
 */
import { departementDepuisCodes, slugTexte, TERRITOIRES_OUTRE_MER } from "@donoor/utils";

/**
 * Collectivités ajoutées au lot 4 (région + département homonymes). Les
 * DROM historiques (971-974, 976) gardent leurs entrées ci-dessous.
 */
const COLLECTIVITES_LOT_4 = new Set(["975", "977", "978", "986", "987", "988"]);
const TERRITOIRES_LOT_4 = TERRITOIRES_OUTRE_MER.filter((t) => COLLECTIVITES_LOT_4.has(t.departement));

/** « en Martinique » → « en », « à La Réunion » → « à » (préposition seule, espace final). */
function prepositionSeule(t: { preposition: string; nom: string }): string {
  const p = t.preposition.endsWith(t.nom) ? t.preposition.slice(0, -t.nom.length) : "en ";
  return p.endsWith(" ") ? p : `${p} `;
}

export interface Department {
  /** Code INSEE : « 01 », « 2A », « 971 »… */
  code: string;
  name: string;
  slug: string;
  regionSlug: string;
}

export interface Region {
  slug: string;
  name: string;
}

export const FRENCH_REGIONS: Region[] = [
  { slug: "auvergne-rhone-alpes", name: "Auvergne-Rhône-Alpes" },
  { slug: "bourgogne-franche-comte", name: "Bourgogne-Franche-Comté" },
  { slug: "bretagne", name: "Bretagne" },
  { slug: "centre-val-de-loire", name: "Centre-Val de Loire" },
  { slug: "corse", name: "Corse" },
  { slug: "grand-est", name: "Grand Est" },
  { slug: "hauts-de-france", name: "Hauts-de-France" },
  { slug: "ile-de-france", name: "Île-de-France" },
  { slug: "normandie", name: "Normandie" },
  { slug: "nouvelle-aquitaine", name: "Nouvelle-Aquitaine" },
  { slug: "occitanie", name: "Occitanie" },
  { slug: "pays-de-la-loire", name: "Pays de la Loire" },
  { slug: "provence-alpes-cote-d-azur", name: "Provence-Alpes-Côte d'Azur" },
  { slug: "guadeloupe", name: "Guadeloupe" },
  { slug: "martinique", name: "Martinique" },
  { slug: "guyane", name: "Guyane" },
  { slug: "la-reunion", name: "La Réunion" },
  { slug: "mayotte", name: "Mayotte" },
  ...TERRITOIRES_LOT_4.map((t) => ({ slug: t.slug, name: t.nom })),
];

const R = {
  ARA: "auvergne-rhone-alpes",
  BFC: "bourgogne-franche-comte",
  BRE: "bretagne",
  CVL: "centre-val-de-loire",
  COR: "corse",
  GES: "grand-est",
  HDF: "hauts-de-france",
  IDF: "ile-de-france",
  NOR: "normandie",
  NAQ: "nouvelle-aquitaine",
  OCC: "occitanie",
  PDL: "pays-de-la-loire",
  PAC: "provence-alpes-cote-d-azur",
  GUA: "guadeloupe",
  MTQ: "martinique",
  GUF: "guyane",
  REU: "la-reunion",
  MAY: "mayotte",
} as const;

/** `code: [nom, région]`. Le slug est dérivé du nom. */
const RAW: Record<string, [string, string]> = {
  "01": ["Ain", R.ARA], "02": ["Aisne", R.HDF], "03": ["Allier", R.ARA],
  "04": ["Alpes-de-Haute-Provence", R.PAC], "05": ["Hautes-Alpes", R.PAC],
  "06": ["Alpes-Maritimes", R.PAC], "07": ["Ardèche", R.ARA],
  "08": ["Ardennes", R.GES], "09": ["Ariège", R.OCC], "10": ["Aube", R.GES],
  "11": ["Aude", R.OCC], "12": ["Aveyron", R.OCC],
  "13": ["Bouches-du-Rhône", R.PAC], "14": ["Calvados", R.NOR],
  "15": ["Cantal", R.ARA], "16": ["Charente", R.NAQ],
  "17": ["Charente-Maritime", R.NAQ], "18": ["Cher", R.CVL],
  "19": ["Corrèze", R.NAQ], "2A": ["Corse-du-Sud", R.COR],
  "2B": ["Haute-Corse", R.COR], "21": ["Côte-d'Or", R.BFC],
  "22": ["Côtes-d'Armor", R.BRE], "23": ["Creuse", R.NAQ],
  "24": ["Dordogne", R.NAQ], "25": ["Doubs", R.BFC], "26": ["Drôme", R.ARA],
  "27": ["Eure", R.NOR], "28": ["Eure-et-Loir", R.CVL],
  "29": ["Finistère", R.BRE], "30": ["Gard", R.OCC],
  "31": ["Haute-Garonne", R.OCC], "32": ["Gers", R.OCC],
  "33": ["Gironde", R.NAQ], "34": ["Hérault", R.OCC],
  "35": ["Ille-et-Vilaine", R.BRE], "36": ["Indre", R.CVL],
  "37": ["Indre-et-Loire", R.CVL], "38": ["Isère", R.ARA],
  "39": ["Jura", R.BFC], "40": ["Landes", R.NAQ],
  "41": ["Loir-et-Cher", R.CVL], "42": ["Loire", R.ARA],
  "43": ["Haute-Loire", R.ARA], "44": ["Loire-Atlantique", R.PDL],
  "45": ["Loiret", R.CVL], "46": ["Lot", R.OCC],
  "47": ["Lot-et-Garonne", R.NAQ], "48": ["Lozère", R.OCC],
  "49": ["Maine-et-Loire", R.PDL], "50": ["Manche", R.NOR],
  "51": ["Marne", R.GES], "52": ["Haute-Marne", R.GES],
  "53": ["Mayenne", R.PDL], "54": ["Meurthe-et-Moselle", R.GES],
  "55": ["Meuse", R.GES], "56": ["Morbihan", R.BRE], "57": ["Moselle", R.GES],
  "58": ["Nièvre", R.BFC], "59": ["Nord", R.HDF], "60": ["Oise", R.HDF],
  "61": ["Orne", R.NOR], "62": ["Pas-de-Calais", R.HDF],
  "63": ["Puy-de-Dôme", R.ARA], "64": ["Pyrénées-Atlantiques", R.NAQ],
  "65": ["Hautes-Pyrénées", R.OCC], "66": ["Pyrénées-Orientales", R.OCC],
  "67": ["Bas-Rhin", R.GES], "68": ["Haut-Rhin", R.GES], "69": ["Rhône", R.ARA],
  "70": ["Haute-Saône", R.BFC], "71": ["Saône-et-Loire", R.BFC],
  "72": ["Sarthe", R.PDL], "73": ["Savoie", R.ARA],
  "74": ["Haute-Savoie", R.ARA], "75": ["Paris", R.IDF],
  "76": ["Seine-Maritime", R.NOR], "77": ["Seine-et-Marne", R.IDF],
  "78": ["Yvelines", R.IDF], "79": ["Deux-Sèvres", R.NAQ],
  "80": ["Somme", R.HDF], "81": ["Tarn", R.OCC],
  "82": ["Tarn-et-Garonne", R.OCC], "83": ["Var", R.PAC],
  "84": ["Vaucluse", R.PAC], "85": ["Vendée", R.PDL], "86": ["Vienne", R.NAQ],
  "87": ["Haute-Vienne", R.NAQ], "88": ["Vosges", R.GES],
  "89": ["Yonne", R.BFC], "90": ["Territoire de Belfort", R.BFC],
  "91": ["Essonne", R.IDF], "92": ["Hauts-de-Seine", R.IDF],
  "93": ["Seine-Saint-Denis", R.IDF], "94": ["Val-de-Marne", R.IDF],
  "95": ["Val-d'Oise", R.IDF], "971": ["Guadeloupe", R.GUA],
  "972": ["Martinique", R.MTQ], "973": ["Guyane", R.GUF],
  "974": ["La Réunion", R.REU], "976": ["Mayotte", R.MAY],
  // Collectivités du lot 4 : département et région homonymes.
  ...Object.fromEntries(TERRITOIRES_LOT_4.map((t) => [t.departement, [t.nom, t.slug] as [string, string]])),
};

/**
 * Slug d'un nom de département : `slugTexte` de @donoor/utils, miroir exact
 * de `donoor_slug_text` (SQL). Identique à l'ancien slug NFD pour tous les
 * noms de la table (« Côte-d'Or » → « cote-d-or »).
 */
export const FRENCH_DEPARTMENTS: Department[] = Object.entries(RAW).map(
  ([code, [name, regionSlug]]) => ({
    code,
    name,
    slug: slugTexte(name),
    regionSlug,
  }),
);

const BY_CODE = new Map(FRENCH_DEPARTMENTS.map((d) => [d.code, d]));
const BY_SLUG = new Map(FRENCH_DEPARTMENTS.map((d) => [d.slug, d]));
const REGION_BY_SLUG = new Map(FRENCH_REGIONS.map((r) => [r.slug, r]));

export function departmentByCode(code: string | null | undefined): Department | null {
  return code ? (BY_CODE.get(code) ?? null) : null;
}

/**
 * Département (ou collectivité d'outre-mer) depuis des codes : INSEE
 * prioritaire (97701 = Saint-Barthélemy), sinon code postal (97133 et 97150
 * avant le préfixe 971). Règle UNIQUE `departementDepuisCodes` (@donoor/utils),
 * miroir de `adresse_departement` (SQL) : pays ≠ FR → null, jamais l'Aube
 * pour « 10117 Berlin ».
 */
export function departmentFromCodes(codes: {
  pays?: string | null;
  codePostal?: string | null;
  codeInsee?: string | null;
}): Department | null {
  return departmentByCode(departementDepuisCodes(codes));
}

export function departmentBySlug(slug: string | null | undefined): Department | null {
  return slug ? (BY_SLUG.get(slug) ?? null) : null;
}

export function regionBySlug(slug: string | null | undefined): Region | null {
  return slug ? (REGION_BY_SLUG.get(slug) ?? null) : null;
}

/**
 * Préposition correcte devant un nom de département : « dans l'Ain »,
 * « en Corrèze », « dans les Landes », « à Paris ».
 *
 * Table EXPLICITE et non heuristique : le genre et le nombre des noms de
 * départements ne se déduisent d'aucune règle fiable (« la Seine-Maritime »
 * mais « le Val-d'Oise », « la Manche » mais « le Morbihan »). Une heuristique
 * produit du français fautif dans le <h1> d'une page dont le seul but est de
 * bien se placer sur « associations dans l'Hérault ». Défaut : « dans le ».
 */
const DEPT_PREPOSITION: Record<string, string> = {
  "75": "à ",
  // dans l' — noms commençant par une voyelle
  "01": "dans l'", "02": "dans l'", "03": "dans l'", "10": "dans l'",
  "11": "dans l'", "12": "dans l'", "27": "dans l'", "34": "dans l'",
  "36": "dans l'", "38": "dans l'", "60": "dans l'", "61": "dans l'",
  "89": "dans l'", "91": "dans l'",
  // en — féminins singuliers
  "07": "en ", "16": "en ", "17": "en ", "19": "en ", "2A": "en ", "2B": "en ",
  "21": "en ", "23": "en ", "24": "en ", "26": "en ", "31": "en ", "33": "en ",
  "35": "en ", "43": "en ", "44": "en ", "48": "en ", "51": "en ", "52": "en ",
  "53": "en ", "54": "en ", "55": "en ", "56": "en ", "57": "en ", "58": "en ",
  "70": "en ", "71": "en ", "72": "en ", "73": "en ", "74": "en ", "76": "en ",
  "77": "en ", "85": "en ", "86": "en ", "87": "en ", "93": "en ",
  // dans la — féminins qui gardent l'article
  "50": "dans la ",
  // dans les — pluriels
  "04": "dans les ", "05": "dans les ", "06": "dans les ", "08": "dans les ",
  "13": "dans les ", "22": "dans les ", "40": "dans les ", "62": "dans les ",
  "64": "dans les ", "65": "dans les ", "66": "dans les ", "78": "dans les ",
  "79": "dans les ", "88": "dans les ", "92": "dans les ",
  // outre-mer
  "971": "en ", "972": "en ", "973": "en ", "974": "à ", "976": "à ",
  ...Object.fromEntries(TERRITOIRES_LOT_4.map((t) => [t.departement, prepositionSeule(t)])),
};

export function departmentPreposition(dept: Department): string {
  return DEPT_PREPOSITION[dept.code] ?? "dans le ";
}

/** « dans l'Hérault », « en Corrèze », « dans les Landes », « à Paris ». */
export function departmentInLabel(dept: Department): string {
  const prep = departmentPreposition(dept);
  // « dans l' » se colle au nom, les autres prennent l'espace déjà présent.
  return `${prep}${dept.name}`;
}

/**
 * Préposition devant un nom de région. Même raison qu'au-dessus : « en
 * Bretagne » mais « dans les Hauts-de-France », « en Île-de-France » mais
 * « dans le Grand Est ».
 */
const REGION_PREPOSITION: Record<string, string> = {
  "auvergne-rhone-alpes": "en ",
  "bourgogne-franche-comte": "en ",
  bretagne: "en ",
  "centre-val-de-loire": "en ",
  corse: "en ",
  "grand-est": "dans le ",
  "hauts-de-france": "dans les ",
  "ile-de-france": "en ",
  normandie: "en ",
  "nouvelle-aquitaine": "en ",
  occitanie: "en ",
  "pays-de-la-loire": "dans les ",
  "provence-alpes-cote-d-azur": "en ",
  guadeloupe: "en ",
  martinique: "en ",
  guyane: "en ",
  "la-reunion": "à ",
  mayotte: "à ",
  ...Object.fromEntries(TERRITOIRES_LOT_4.map((t) => [t.slug, prepositionSeule(t)])),
};

/** « en Bretagne », « dans les Hauts-de-France », « à La Réunion ». */
export function regionInLabel(region: Region): string {
  const prep = REGION_PREPOSITION[region.slug] ?? "en ";
  // « à La Réunion » : le nom porte déjà son article.
  return `${prep}${region.name}`;
}
